'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { cn } from '@ais-ui/utils';
import { Badge } from './Badge';
import {
  PiAnchor,
  PiCalendar,
  PiGavel,
  PiHandPalm,
  PiListChecks,
  PiQuestion,
} from 'react-icons/pi';
import { IconType } from 'react-icons/lib';

// Define event type structure
export type EventType = {
  id: string;
  name: string;
  color: string;
  disabled: boolean;
};

export type TimelineEvent = {
  id: string;
  date: Date;
  title: string;
  description: string;
  type: EventType;
  metadata?: Record<string, string>;
};

type Cluster = {
  id: string;
  position: number;
  events: TimelineEvent[];
  types: EventType[]; // Unique event types in this cluster
  startDate: Date;
  endDate: Date;
};

export type TimelineProps = {
  events: TimelineEvent[];
  eventTypes: EventType[]; // All possible event types
  className?: string;
  clusterThreshold?: number; // Distance threshold for clustering (in percentage)
  durationDays?: number; // New prop: timeline duration in days
};

interface IconMapperProps {
  iconKey: string;
  size?: number;
  color?: string;
  className?: string;
}

const IconMapper = ({
  iconKey,
  size = 12,
  color = 'inherit',
  className = '',
}: IconMapperProps) => {
  // Icon mapping object
  const iconMap: Record<string, IconType> = {
    'port-call': PiAnchor,
    inspection: PiListChecks,
    ban: PiHandPalm,
    sanction: PiGavel,
  };

  // Get the icon component from the map, or use HelpCircle as fallback
  const IconComponent = iconMap[iconKey.toLowerCase()] || PiQuestion;

  return (
    <IconComponent
      className={`h-${size} w-${size} ${className}`}
      style={{ color }}
    />
  );
};

export function Timeline({
  events,
  eventTypes,
  className,
  clusterThreshold = 5,
  durationDays,
}: TimelineProps) {
  // Filter events based on the specified duration
  const filteredEvents = useMemo(() => {
    if (!durationDays) {
      return events; // Return all events if no duration specified
    }

    // Calculate the start date based on duration (end date is now)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - durationDays);

    // Filter events within the date range
    return events.filter(
      (event) => event.date >= startDate && event.date <= endDate
    );
  }, [events, durationDays]);

  const sortedEvents = useMemo(
    () =>
      [...filteredEvents].sort((a, b) => a.date.getTime() - b.date.getTime()),
    [filteredEvents]
  );

  const timelineRef = useRef<HTMLDivElement>(null);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [viewingCluster, setViewingCluster] = useState(false);
  // State for label positions
  const [labelPositions, setLabelPositions] = useState<Record<string, number>>(
    {}
  );

  // Calculate the fixed timeline start and end dates based on duration
  const timelineDates = useMemo(() => {
    const endDate = new Date(); // Timeline ends at current date
    const startDate = new Date();

    if (durationDays) {
      startDate.setDate(endDate.getDate() - durationDays);
      return { startDate, endDate };
    } else if (sortedEvents.length > 0) {
      // If no duration specified but we have events, use event dates
      return {
        startDate: sortedEvents[0].date,
        endDate: sortedEvents[sortedEvents.length - 1].date,
      };
    } else {
      // Default: 30 days if no events and no duration specified
      startDate.setDate(endDate.getDate() - 30);
      return { startDate, endDate };
    }
  }, [sortedEvents, durationDays]);

  // Calculate positions and create clusters - memoized to prevent recalculation on every render
  const clusters = useMemo(() => {
    if (sortedEvents.length <= 1) {
      if (sortedEvents.length === 1) {
        // Calculate position based on the fixed timeline range
        const position = calculatePositionInTimeline(
          sortedEvents[0].date,
          timelineDates.startDate,
          timelineDates.endDate
        );

        return [
          {
            id: `cluster-${sortedEvents[0].id}`,
            position,
            events: [sortedEvents[0]],
            types: [sortedEvents[0].type],
            startDate: sortedEvents[0].date,
            endDate: sortedEvents[0].date,
          },
        ];
      }
      return [];
    }

    const positions: Record<string, number> = {};
    // Calculate position for each event relative to the fixed timeline
    sortedEvents.forEach((event) => {
      positions[event.id] = calculatePositionInTimeline(
        event.date,
        timelineDates.startDate,
        timelineDates.endDate
      );
    });

    // Group events into clusters based on proximity
    const clusters: Cluster[] = [];
    const assignedEvents = new Set<string>();

    for (let i = 0; i < sortedEvents.length; i++) {
      const event = sortedEvents[i];
      if (assignedEvents.has(event.id)) continue;

      const clusterEvents = [event];
      assignedEvents.add(event.id);
      const eventPosition = positions[event.id];

      // Find nearby events
      for (let j = 0; j < sortedEvents.length; j++) {
        if (i === j) continue;
        const otherEvent = sortedEvents[j];
        if (assignedEvents.has(otherEvent.id)) continue;

        const otherPosition = positions[otherEvent.id];
        if (Math.abs(eventPosition - otherPosition) <= clusterThreshold) {
          clusterEvents.push(otherEvent);
          assignedEvents.add(otherEvent.id);
        }
      }

      // Sort cluster events by date
      clusterEvents.sort((a, b) => a.date.getTime() - b.date.getTime());

      // Calculate average position for the cluster
      const clusterPosition =
        clusterEvents.reduce((sum, e) => sum + positions[e.id], 0) /
        clusterEvents.length;

      // Extract unique types
      const uniqueTypes: EventType[] = [];
      const typeIds = new Set<string>();

      clusterEvents.forEach((event) => {
        if (!typeIds.has(event.type.id)) {
          typeIds.add(event.type.id);
          uniqueTypes.push(event.type);
        }
      });

      // Get the date range
      const startDate = clusterEvents[0].date;
      const endDate = clusterEvents[clusterEvents.length - 1].date;

      clusters.push({
        id: `cluster-${clusters.length}`,
        position: clusterPosition,
        events: clusterEvents,
        types: uniqueTypes,
        startDate,
        endDate,
      });
    }

    return clusters;
  }, [sortedEvents, clusterThreshold, timelineDates]); // Added timelineDates as dependency

  // Helper function to calculate position percentage in the timeline
  function calculatePositionInTimeline(
    date: Date,
    startDate: Date,
    endDate: Date
  ): number {
    const dateTime = date.getTime();
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    const timeRange = endTime - startTime;

    // If the time range is 0 (single point in time), return center position
    if (timeRange === 0) return 50;

    // Calculate percentage position (clamped between 0-100)
    const position = ((dateTime - startTime) / timeRange) * 100;
    return Math.max(0, Math.min(100, position));
  }

  // Effect for label position calculation
  useEffect(() => {
    if (!timelineRef.current || clusters.length === 0) return;

    // Calculate the positions after the timeline is rendered
    const resolveOverlaps = () => {
      const timelineWidth = timelineRef.current?.clientWidth || 0;
      const labelWidths: Record<string, number> = {};
      const labelXPositions: Record<string, number> = {};
      const newPositions: Record<string, number> = {};

      // First pass: Calculate all label widths and positions
      clusters.forEach((cluster) => {
        // Estimate label width based on date format (can be improved with actual measurements)
        const dateText = formatDateRange(cluster.startDate, cluster.endDate);
        const estimatedWidth = dateText.length * 6; // Rough estimate of 6px per character

        labelWidths[cluster.id] = estimatedWidth;
        labelXPositions[cluster.id] = (cluster.position / 100) * timelineWidth;

        // Start with default row (0 = top row)
        newPositions[cluster.id] = 0;
      });

      // Second pass: Resolve overlaps using multiple rows
      const rowOverlapMap: Record<number, { start: number; end: number }[]> = {
        0: [],
      };

      clusters.forEach((cluster) => {
        const labelWidth = labelWidths[cluster.id];
        const xPos = labelXPositions[cluster.id];
        const labelLeft = xPos - labelWidth / 2;
        const labelRight = xPos + labelWidth / 2;

        // Find a row where this label doesn't overlap
        let row = 0;
        let hasOverlap = true;

        while (hasOverlap) {
          hasOverlap = false;

          // Check if current row exists in map
          if (!rowOverlapMap[row]) {
            rowOverlapMap[row] = [];
          }

          // Check for overlaps in this row
          for (const range of rowOverlapMap[row]) {
            if (!(labelRight < range.start || labelLeft > range.end)) {
              hasOverlap = true;
              break;
            }
          }

          if (hasOverlap) {
            row++; // Try next row
          } else {
            // No overlap, add to this row
            rowOverlapMap[row].push({
              start: labelLeft,
              end: labelRight,
            });
            newPositions[cluster.id] = row;
          }
        }
      });

      setLabelPositions(newPositions);
    };

    // Run once after initial render and when clusters change
    resolveOverlaps();

    // Also run when window resizes
    const handleResize = () => {
      resolveOverlaps();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clusters, timelineRef.current?.clientWidth]); // Add timelineRef width as dependency

  // Format date range (e.g., "Mar 9-15, 2025" or just "Mar 9, 2025" if same day)
  const formatDateRange = (startDate: Date, endDate: Date) => {
    const sameDay =
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();

    if (sameDay) {
      return format(startDate, 'MMM d, yyyy');
    }

    const sameMonth =
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();

    if (sameMonth) {
      return `${format(startDate, 'MMM d')}-${format(endDate, 'd, yyyy')}`;
    }

    const sameYear = startDate.getFullYear() === endDate.getFullYear();

    if (sameYear) {
      return `${format(startDate, 'MMM d')}-${format(endDate, 'MMM d, yyyy')}`;
    }

    return `${format(startDate, 'MMM d, yyyy')}-${format(
      endDate,
      'MMM d, yyyy'
    )}`;
  };

  // Handle cluster click
  const handleClusterClick = (clusterId: string) => {
    const cluster = clusters.find((c) => c.id === clusterId);
    if (!cluster) return;

    setSelectedCluster(clusterId);

    if (cluster.events.length === 1) {
      // If cluster has only one event, select it directly
      setSelectedEvent(cluster.events[0].id);
      setViewingCluster(false);
    } else {
      // If cluster has multiple events, show cluster view
      setViewingCluster(true);
      setSelectedEvent(null);
    }
  };

  // Handle event selection within a cluster
  const handleEventSelect = (eventId: string) => {
    setSelectedEvent(eventId);
    setViewingCluster(false);
  };

  // Handle back to cluster view
  const handleBackToCluster = () => {
    setViewingCluster(true);
    setSelectedEvent(null);
  };

  const selectedClusterData = selectedCluster
    ? clusters.find((c) => c.id === selectedCluster)
    : null;

  const selectedEventData = selectedEvent
    ? sortedEvents.find((e) => e.id === selectedEvent)
    : null;

  // Function to render the cluster marker based on its contents
  const renderClusterMarker = (cluster: Cluster, isSelected: boolean) => {
    const size = cluster.events.length > 1 ? 6 : 4; // Base size value
    const dimensionPx = `${size * 4}px`; // Convert to pixels, consistent for both types

    // Single-type cluster (either one event or all same type)
    if (cluster.types.length === 1) {
      return (
        <button
          onClick={() => handleClusterClick(cluster.id)}
          className={cn(
            'rounded-full border-2 transition-all duration-200 -translate-x-1/2 flex items-center justify-center',
            isSelected ? 'scale-125' : 'bg-background hover:scale-110'
          )}
          style={{
            width: dimensionPx,
            height: dimensionPx,
            borderColor: cluster.types[0].color,
            backgroundColor: isSelected
              ? cluster.types[0].color
              : 'var(--background)',
          }}
          aria-label={`View ${
            cluster.events.length > 1 ? 'clustered events' : 'event'
          }`}
        >
          {cluster.events.length > 1 && (
            <span
              className={cn(
                'text-xs font-bold',
                isSelected ? 'text-white' : 'text-foreground'
              )}
            >
              {cluster.events.length}
            </span>
          )}
        </button>
      );
    }

    // Multi-type cluster - create a pie chart style button
    return (
      <button
        onClick={() => handleClusterClick(cluster.id)}
        className={cn(
          'rounded-full transition-all duration-200 -translate-x-1/2 flex items-center justify-center overflow-hidden border-white',
          isSelected ? 'scale-125' : 'hover:scale-110'
        )}
        style={{
          width: dimensionPx, // Same dimension as single-type
          height: dimensionPx, // Same dimension as single-type
          boxShadow: isSelected ? '0 0 0 2px rgba(255,255,255,0.5)' : 'none',
        }}
        aria-label={`View ${cluster.events.length} clustered events`}
      >
        {/* Color segments */}
        <div className="w-full h-full relative">
          {cluster.types.length === 2 ? (
            // For two types, do a simple split
            <>
              <div
                className="absolute top-0 left-0 w-1/2 h-full"
                style={{ backgroundColor: cluster.types[0].color }}
              />
              <div
                className="absolute top-0 right-0 w-1/2 h-full"
                style={{ backgroundColor: cluster.types[1].color }}
              />
            </>
          ) : (
            // For three or more types, do a pie-chart style division
            cluster.types.map((type, idx) => {
              const angle = 360 / cluster.types.length;
              const rotation = idx * angle;
              return (
                <div
                  key={idx}
                  className="absolute top-0 left-0 w-full h-full origin-center"
                  style={{
                    backgroundColor: type.color,
                    clipPath: `polygon(50% 50%, 50% 0%, ${
                      50 + 50 * Math.cos(((rotation + angle) * Math.PI) / 180)
                    }% ${
                      50 + 50 * Math.sin(((rotation + angle) * Math.PI) / 180)
                    }%, ${50 + 50 * Math.cos((rotation * Math.PI) / 180)}% ${
                      50 + 50 * Math.sin((rotation * Math.PI) / 180)
                    }%)`,
                  }}
                />
              );
            })
          )}

          {/* Count overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
            <span className="text-xs font-bold text-white">
              {cluster.events.length}
            </span>
          </div>
        </div>
      </button>
    );
  };

  // Calculate marker size for consistent label positioning
  const getMarkerSize = (cluster: Cluster) => {
    const baseSize = cluster.events.length > 1 ? 6 : 4;
    return baseSize * 4; // Matches the dimensionPx calculation in renderClusterMarker
  };

  return (
    <div className={cn('w-full py-8', className)}>
      <div className="px-4">
        <div
          ref={timelineRef}
          className="relative h-1 w-full bg-muted rounded-full mb-12"
        >
          {/* Cluster points with dynamically positioned labels */}
          {clusters.map((cluster) => {
            const isSelected = selectedCluster === cluster.id;
            const row = labelPositions[cluster.id] || 0;
            const markerSize = getMarkerSize(cluster);

            // Calculate label offset based on marker size
            // Add half of marker size to ensure consistent positioning
            const labelTopOffset = markerSize / 2 + 12 + row * 14;

            return (
              <div
                key={cluster.id}
                className="absolute top-1/2 -translate-y-1/2"
                style={{ left: `${cluster.position}%` }}
              >
                {renderClusterMarker(cluster, isSelected)}
                <div
                  className={cn(
                    'absolute left-1/2 -translate-x-1/2 whitespace-nowrap pt-1 text-xs font-medium transition-all duration-200',
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  )}
                  style={{
                    color:
                      isSelected && cluster.types.length === 1
                        ? cluster.types[0].color
                        : isSelected
                        ? 'var(--primary)'
                        : 'var(--muted-foreground)',
                    top: `${labelTopOffset}px`,
                  }}
                >
                  {formatDateRange(cluster.startDate, cluster.endDate)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Event details */}
      <div className="pt-4">
        {selectedClusterData ? (
          <div className="min-h-[264px]">
            {viewingCluster && selectedClusterData.events.length > 1 && (
              <div className="transition-opacity duration-300 opacity-100">
                <h3 className="text-md font-medium mb-2">
                  {formatDateRange(
                    selectedClusterData.startDate,
                    selectedClusterData.endDate
                  )}{' '}
                  - {selectedClusterData.events.length} Events
                </h3>
                <ul className="grid grid-cols-4 gap-2 w-full">
                  {selectedClusterData.events.map((event) => (
                    <li
                      key={event.id}
                      className="p-2 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50"
                      onClick={() => handleEventSelect(event.id)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: event.type.color }}
                        ></div>
                        <div className="flex-1">
                          <span className="font-medium">{event.title}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {event.type.name}
                          </span>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground pl-5">
                        {format(event.date, 'MMMM d, yyyy')}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {selectedEventData && !viewingCluster && (
              <div className="w-[660px]">
                {selectedClusterData.events.length > 1 && (
                  <button
                    onClick={handleBackToCluster}
                    className="text-xs text-muted-foreground hover:text-foreground pb-2 flex items-center"
                  >
                    ← Back to all events
                  </button>
                )}
                <div className="flex justify-between">
                  <div className="transition-opacity duration-300 opacity-100 space-y-1">
                    <Badge
                      style={{ backgroundColor: selectedEventData.type.color }}
                    >
                      {selectedEventData.type.name}
                    </Badge>
                    <h3
                      className="text-md font-medium"
                      style={{
                        color: selectedEventData.type.color || 'inherit',
                      }}
                    >
                      {selectedEventData.title}
                    </h3>
                    <div className="flex space-x-1 text-muted-foreground">
                      <PiCalendar></PiCalendar>
                      <p className="text-xs mb-2">
                        {format(selectedEventData.date, 'MMMM d, yyyy')}
                      </p>
                    </div>
                    <p className="text-sm">{selectedEventData.description}</p>
                  </div>
                  <div className="hidden md:block">
                    <IconMapper
                      iconKey={selectedEventData.type.id}
                      color={selectedEventData.type.color || 'inherit'}
                    />
                  </div>
                </div>
                <div>
                  {selectedEventData.metadata &&
                    Object.keys(selectedEventData.metadata).length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <dl className="grid grid-cols-2 gap-3">
                          {Object.entries(selectedEventData.metadata).map(
                            ([key, value]) => (
                              <div key={key} className="col-span-1">
                                <dt className="text-xs font-medium text-muted-foreground">
                                  {key}
                                </dt>
                                <dd className="text-sm">{value}</dd>
                              </div>
                            )
                          )}
                        </dl>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="min-h-[264px] flex items-center justify-center w-full text-muted-foreground">
            Click the points on the timeline to view details.
          </div>
        )}
      </div>
    </div>
  );
}
